import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE_URL } from '@/utils/apiClient';

const VAPID_PUBLIC_KEY = 'BBeXItwhbSLSMczy1rcnGxqXc1m9SKBT4OpI1jL7_rFWxWW58VS5KfEnjDTRU_Ovca5fhA6MKmv4xVOLGBFKsCM';

// Sincroniza la suscripción con el backend UNA sola vez por carga de página
let _syncedThisLoad = false;

// Convert URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(false);

  // Play beep sound via Web Audio API
  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(880, ctx.currentTime);
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + 0.3);
    } catch (e) {
      // Audio not available, silently ignore
    }
  };

  // Check if push notifications are supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    }
  }, []);

  // Re-sincroniza con el backend al cargar (sesión ya abierta, app actualizada,
  // o el backend perdió/desactivó la suscripción). Idempotente, 1 vez por carga.
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (_syncedThisLoad || !token || !isSupported || Notification.permission !== 'granted') return;
    _syncedThisLoad = true;
    (async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
        let sub = await registration.pushManager.getSubscription();
        if (!sub) {
          sub = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
          });
        }
        await axios.post(`${API_BASE_URL}/api/notifications/subscribe`, {
          subscription: sub.toJSON()
        }, { headers: { Authorization: `Bearer ${token}` } });
        setIsSubscribed(true);
      } catch (e) {
        // silencioso: se reintenta en la próxima carga
      }
    })();
  }, [isSupported]);

  // Listen for SW messages (beep trigger)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data && event.data.type === 'PLAY_BEEP') {
        playBeep();
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  // Check current subscription status
  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  // Register service worker
  const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service workers not supported');
    }
    
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      // SW registered;
      return registration;
    } catch (error) {
      console.error('Error registering SW:', error);
      throw error;
    }
  };

  // Request notification permission
  const requestPermission = async () => {
    if (!isSupported) {
      toast.error('Tu navegador no soporta notificaciones push');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        toast.success('¡Notificaciones activadas!');
        return true;
      } else if (result === 'denied') {
        toast.error('Notificaciones bloqueadas. Actívalas en la configuración del navegador.');
        return false;
      }
      return false;
    } catch (error) {
      console.error('Error requesting permission:', error);
      return false;
    }
  };

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      toast.error('Notificaciones push no soportadas');
      return null;
    }

    setLoading(true);
    
    try {
      // Request permission first
      if (permission !== 'granted') {
        const granted = await requestPermission();
        if (!granted) {
          setLoading(false);
          return null;
        }
      }

      // Register service worker
      const registration = await registerServiceWorker();
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Reutilizar la suscripción existente o crear una nueva
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });
      }

      // Push subscription active;

      // Send subscription to backend
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API_BASE_URL}/api/notifications/subscribe`, {
          subscription: subscription.toJSON()
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setIsSubscribed(true);
      toast.success('¡Notificaciones push activadas!');
      return subscription;
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Error al activar notificaciones');
      return null;
    } finally {
      setLoading(false);
    }
  }, [isSupported, permission]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        
        // Notify backend
        const token = localStorage.getItem('token');
        if (token) {
          await axios.post(`${API_BASE_URL}/api/notifications/unsubscribe`, {
            endpoint: subscription.endpoint
          }, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => {});
        }
      }

      setIsSubscribed(false);
      toast.info('Notificaciones desactivadas');
    } catch (error) {
      console.error('Error unsubscribing:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Show local notification (for testing or fallback)
  const showLocalNotification = useCallback((title, body, options = {}) => {
    if (permission !== 'granted') {
      toast.info(body);
      return;
    }

    const notification = new Notification(title, {
      body,
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'viadirecta-local',
      ...options
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.url) {
        window.location.href = options.url;
      }
    };
  }, [permission]);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    subscribe,
    unsubscribe,
    requestPermission,
    showLocalNotification
  };
}

// Notification prompt component
export function NotificationPrompt({ onClose }) {
  const { isSupported, isSubscribed, subscribe, loading } = usePushNotifications();

  if (!isSupported || isSubscribed) return null;

  let isDriver = false;
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    isDriver = u.role === 'driver' || u.driver_status === 'active';
  } catch (e) { /* ignore */ }

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 p-4 z-50 animate-in slide-in-from-bottom-4" data-testid="push-prompt">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-slate-900 dark:text-white text-sm">
            Activa las notificaciones
          </h4>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {isDriver
              ? 'Sin notificaciones te pierdes los viajes disponibles para aceptar — el primero que acepta se lo lleva.'
              : 'Recibe alertas cuando tu conductor inicie el viaje o esté cerca de ti.'}
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={subscribe}
              disabled={loading}
              data-testid="push-prompt-activate-btn"
              className="flex-1 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Activando...' : 'Activar'}
            </button>
            <button
              onClick={onClose}
              data-testid="push-prompt-dismiss-btn"
              className="px-3 py-2 text-slate-500 hover:text-slate-700 text-xs font-medium"
            >
              Ahora no
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default usePushNotifications;
